<template>
  <Dashboard :pin-profile-sidebar="activeTab === 'queue'">
    <!-- Keep the panel unmounted without matchmaking access so it never fetches
         play modes/regions while onMounted redirects them away -->
    <div v-if="canUseMatchmaking && !auth.user()?.matchmaking_lan_only" class="max-w-7xl mx-auto flex flex-col gap-10">
      <header class="flex flex-col gap-3">
        <div class="flex flex-wrap items-center justify-between gap-4">
          <h1 class="text-3xl sm:text-4xl font-bold text-text-primary">{{ $t('play.matchmaking.title') }}</h1>
          <div class="flex rounded-lg bg-gray-800 p-1 text-sm">
            <button
              v-for="tab in ['queue', 'lan']"
              :key="tab"
              type="button"
              class="px-4 py-2 rounded-md font-semibold transition-colors"
              :class="activeTab === tab ? 'bg-gray-700 text-white' : 'text-text-secondary hover:text-text-primary'"
              @click="showTab(tab)"
            >{{ tab === 'queue' ? $t('play.lan_party.queue_tab') : $t('play.lan_party.lan_parties_tab') }}</button>
          </div>
        </div>
        <p class="text-base text-text-secondary max-w-2xl">{{ $t('play.matchmaking.intro') }}</p>
      </header>

      <QueueSelectionPanel v-if="activeTab === 'queue'" @invite="showInviteModal = true" />
      <LanPartiesList v-else />
    </div>

    <InviteFriendsModal :open="showInviteModal" @close="showInviteModal = false" />

    <!-- Join Party Modal (from lobby link) -->
    <div
      v-if="joinPartyUuid"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
    >
      <div
        class="relative w-full max-w-sm mx-4 rounded-2xl p-8 text-center"
        style="background: #0D0F14; border: 1px solid #1E2733;"
      >
        <h2 class="text-2xl font-bold text-white mb-2">{{ $t('play.matchmaking.join_party_prompt') }}</h2>
        <p class="text-gray-400 text-sm mb-6">{{ $t('play.matchmaking.invited_via_link') }}</p>
        <div class="flex gap-3">
          <button
            @click="clearJoinLink"
            class="flex-1 px-4 py-3 rounded-xl bg-gray-800 text-text-primary text-base font-semibold hover:bg-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
          >
            {{ $t('play.matchmaking.decline') }}
          </button>
          <button
            @click="joinParty"
            :disabled="joining"
            class="flex-1 px-4 py-3 rounded-xl bg-primary-600 text-white text-base font-semibold shadow-[0_0_24px_rgba(78,84,200,0.35)] hover:bg-primary-500 transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
          >
            {{ joining ? $t('play.matchmaking.joining_party') : $t('play.matchmaking.join_party') }}
          </button>
        </div>
        <p v-if="joinError" class="text-red-400 text-xs mt-3">{{ joinError }}</p>
      </div>
    </div>
  </Dashboard>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { useHead } from '@unhead/vue';
import Dashboard from '../components/layouts/DashboardLayout.vue';
import QueueSelectionPanel from '../components/Matchmaking/QueueSelectionPanel.vue';
import InviteFriendsModal from '../components/Matchmaking/InviteFriendsModal.vue';
import LanPartiesList from '../components/LanParty/LanPartiesList.vue';

const { t } = useI18n();
const auth = useAuth();
const user = ref(auth.user());
const store = useStore();
const route = useRoute();
const router = useRouter();

const canUseMatchmaking = computed(() => !!auth.user()?.can_use_matchmaking);

const showInviteModal = ref(false);
const activeTab = computed(() => (route.name === 'MatchmakingLanPage' ? 'lan' : 'queue'));
useHead({
  title: computed(() => (activeTab.value === 'lan'
    ? t('play.lan_party.list_page_title')
    : t('play.matchmaking.page_title'))),
});

function showTab(tab) {
  router.replace({ name: tab === 'lan' ? 'MatchmakingLanPage' : 'MatchmakingPage', query: route.query });
}

// Join via link
const joinPartyUuid = ref(null);
const joining = ref(false);
const joinError = ref('');

function clearJoinLink() {
  joinPartyUuid.value = null;
  router.replace({ query: {} });
}

function joinParty() {
  if (!joinPartyUuid.value) return;
  joining.value = true;
  joinError.value = '';
  axios
    .post(`/matchmaking/parties/${joinPartyUuid.value}/join`)
    .then(({ data }) => {
      store.commit('setMatchmakingParty', data);
      clearJoinLink();
    })
    .catch((err) => {
      joinError.value = err.response?.data?.error || t('play.matchmaking.join_party_failed');
    })
    .finally(() => {
      joining.value = false;
    });
}

onMounted(async () => {
  if (!canUseMatchmaking.value) {
    await auth.fetch().catch(() => {});
    if (!canUseMatchmaking.value) {
      router.push('/');
      return;
    }
  }
  // In through a LAN party only: their night is the whole of matchmaking for now.
  if (auth.user()?.matchmaking_lan_only) {
    router.replace({ name: 'LanHubPage' });
    return;
  }

  if (route.query.join) {
    joinPartyUuid.value = route.query.join;
  }
});
</script>
